import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface IExportDownload {
	url: string;
	fileName: string;
}

/**
 * The signed url carries `Content-Disposition: attachment` and the file name, so handing it to the
 * browser downloads in place and leaves the page where it is.
 *
 * Deliberately not file-saver: for a string url it first probes CORS, and the S3 endpoint these urls are
 * signed against sends no CORS headers, so it falls back to `window.open` and the download arrives via a
 * stray tab.
 */
function startDownload(url: string) {
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.rel = 'noopener';
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
}

/**
 * Mints a short-lived download for an export job and starts it.
 *
 * A mutation rather than a query because the URL expires in minutes — caching it, or refetching it on
 * focus, would hand back a link that has already died.
 */
export function useSystemJobDownloadUrlMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({ jobId }: { jobId: string }) => {
			// An export started under admin impersonation belongs to the impersonated account, so it can only
			// be found through the same impersonation route.
			const download = await userSession.webServiceHelper.callAsync<IExportDownload>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `systemJob/${encodeURIComponent(jobId)}/downloadUrl`,
				}),
				'GET'
			);

			// callAsync resolves a 200 with an absent body to undefined, which would otherwise look like a
			// successful download that saved nothing.
			if (!download?.url) {
				// Carries the IOperationResultNoValue shape as well, so pushApiError still reads the message.
				throw Object.assign(new Error('The export download could not be prepared.'), {
					success: false,
					systemMessage: 'The export download could not be prepared.',
				});
			}

			startDownload(download.url);
			return download;
		},
		onError,
		onSuccess: () => onSuccess?.(),
	});
}
